// Browser presenter: frame-counted timing with requestAnimationFrame, responses from the
// keyboard (this window) or the operator console (BroadcastChannel), timestamped on the
// performance clock so reaction times are measured from the frame the E was drawn on.

import type { ResponseKey, Timing } from "../lib/protocol";
import type { ERenderer } from "../render/eRenderer";
import type { FrameStats, PresentResult, Presenter, Stimulus } from "./runner";

export interface QueuedResponse {
  key: ResponseKey;
  t: number; // performance.now() timebase of this window
}

const KEYMAP: Record<string, ResponseKey> = {
  ArrowUp: "up", ArrowDown: "down", ArrowLeft: "left", ArrowRight: "right",
  Numpad8: "up", Numpad2: "down", Numpad4: "left", Numpad6: "right",
  KeyW: "up", KeyS: "down", KeyA: "left", KeyD: "right",
  Space: "none", Numpad0: "none",
};

export class ResponseQueue {
  private items: QueuedResponse[] = [];
  private waiters: Array<() => void> = [];

  push(r: QueuedResponse): void {
    this.items.push(r);
    this.waiters.splice(0).forEach((w) => w());
  }

  /** Remote responses carry epoch milliseconds; convert to this window's clock. */
  pushEpoch(key: ResponseKey, epochMs: number): void {
    this.push({ key, t: epochMs - performance.timeOrigin });
  }

  clear(): void {
    this.items = [];
  }

  take(since: number): QueuedResponse | null {
    while (this.items.length) {
      const r = this.items.shift()!;
      if (r.t >= since) return r;
    }
    return null;
  }

  attachKeyboard(target: Window): () => void {
    const onKey = (e: KeyboardEvent) => {
      const k = KEYMAP[e.code];
      if (!k) return;
      e.preventDefault();
      if (e.repeat) return;
      this.push({ key: k, t: e.timeStamp });
    };
    target.addEventListener("keydown", onKey);
    return () => target.removeEventListener("keydown", onKey);
  }
}

const nextFrame = (): Promise<number> => new Promise((r) => requestAnimationFrame(r));

export class RafPresenter implements Presenter {
  constructor(
    private readonly renderer: ERenderer,
    private readonly responses: ResponseQueue,
    private readonly outputBits: number,
    private readonly dither = 0,
  ) {}

  blank(backgroundCode: number, backgroundRgb?: [number, number, number]): void {
    this.renderer.draw({ show: false, orientation: "right", strokePx: 1, backgroundCode,
      letterCode: backgroundCode, backgroundRgb: backgroundRgb ?? null, dither: this.dither }, this.outputBits);
  }

  async present(stim: Stimulus, timing: Timing, signal: AbortSignal): Promise<PresentResult> {
    // inter-stimulus interval on the background
    let t0 = await nextFrame();
    this.blank(stim.backgroundCode, stim.backgroundRgb);
    while (!signal.aborted) {
      const t = await nextFrame();
      if (t - t0 >= timing.isi_ms) break;
      this.blank(stim.backgroundCode, stim.backgroundRgb);
    }
    // stimulus on: drawn in this frame's callback; onset = this callback's timestamp
    const onset = await nextFrame();
    this.renderer.draw({ show: true, ...stim, dither: this.dither }, this.outputBits);
    this.responses.clear();
    const intervals: number[] = [];
    let last = onset;
    let visible = true;
    let got: QueuedResponse | null = null;
    t0 = onset;
    while (!signal.aborted) {
      const t = await nextFrame();
      intervals.push(t - last);
      last = t;
      got = this.responses.take(onset);
      if (got) break;
      const elapsed = t - t0;
      if (visible && timing.stimulus_ms > 0 && elapsed >= timing.stimulus_ms) {
        visible = false;
        this.blank(stim.backgroundCode, stim.backgroundRgb);
      } else if (visible) {
        this.renderer.draw({ show: true, ...stim, dither: this.dither }, this.outputBits);
      }
      if (timing.response_timeout_ms > 0 && elapsed >= timing.response_timeout_ms) break;
    }
    this.blank(stim.backgroundCode, stim.backgroundRgb);
    return {
      response: got?.key ?? "none",
      rtMs: got ? Math.round((got.t - onset) * 10) / 10 : null,
      frame: frameStats(intervals),
    };
  }
}

export function frameStats(intervals: number[]): FrameStats | null {
  if (!intervals.length) return null;
  const s = [...intervals].sort((a, b) => a - b);
  const median = s[Math.floor(s.length / 2)]!;
  return {
    frames: intervals.length,
    median_ms: Math.round(median * 100) / 100,
    max_ms: Math.round(s[s.length - 1]! * 100) / 100,
    dropped: intervals.filter((d) => d > 1.5 * median).length,
  };
}
